import type {ReactNode} from "react";
import type {CheckboxProps, CheckboxSize} from "@reside/ui";
import {ChecklistItem} from "@reside/reside-api-admission";

import type {StyledDropdownSelectOption} from "./styles";

const {CheckedStatusEnum} = ChecklistItem;
export {CheckedStatusEnum};

export type NotApplicableStatusOption = {
  icon: ReactNode;
  active: boolean;
  onClick: () => void;
  StyledDropdownSelectOption: typeof StyledDropdownSelectOption;
};

export type TaskCheckboxChildren = {
  Checkbox: (props: Partial<CheckboxProps>) => JSX.Element;
  Label: () => JSX.Element;
  FormattedDate: (props: any) => JSX.Element;
  name: string;
  hasCheckedStatus: boolean;
};

export type FieldValue = Pick<
  ChecklistItem,
  "checked" | "checkedDate" | "checkedStatus"
>;

export type TaskCheckboxFieldDefaultProps = {
  tooltipLength: number;
  Checkbox: React.ComponentType<any>;
  CheckboxWrapper: React.ComponentType;
  ClickOutsideWrapper: React.ComponentType;
  Label: React.ComponentType<{disabled?: boolean; isOpened: boolean}>;
  LabelText: React.ComponentType<{strikeThrough: boolean}>;
  DatePicker: React.ComponentType<any>;
  children: (props: TaskCheckboxChildren) => JSX.Element;
  /** Renders beside the status caret inside `[data-task-checkbox-trailing]` (e.g. checklist completion date). */
  renderTrailingWithCaret?: (props: TaskCheckboxChildren) => ReactNode;
  renderNotApplicableStatusOption: (
    props: NotApplicableStatusOption,
  ) => JSX.Element;
};

/**
 * @property {string} label The label for the task checkbox field.
 * @property {string} name The formik field name (state key).
 * @property {CheckboxSize} [size] The size of the checkbox.
 * @property {boolean} [disabled=false] Whether the task checkbox field is disabled.
 * @property {({setCheckboxClosed, setStatusCompleted}: {setCheckboxClosed: () => void; setStatusCompleted: () => void;}) => void} [onComplete] Callback function triggered when the task is completed.
 */
export type TaskCheckboxFieldProps = Partial<TaskCheckboxFieldDefaultProps> & {
  label: string;
  name: string;
  size?: CheckboxSize;
  /** Pixel width/height of the checkbox square (`taskCheckboxFadedSmallOverrides`). Default 14 (card); use 16 in modals. */
  checkboxBoxPx?: 14 | 16;
  disabled?: boolean;
  onComplete?: ({
    setCheckboxClosed,
    setStatusCompleted,
  }: {
    setCheckboxClosed: () => void;
    setStatusCompleted: () => void;
  }) => void;
};
